import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import S3MultipleFileUpload from '../components/S3MultipleFileUpload';
import { saveDraft, isQuotaError } from '../lib/offlineDb';
import { triggerSync } from '../lib/inspectionSyncEngine';
import { certificateTypeFromServiceName } from '../utils/certificateTypes';

const OFFLINE_MAX_SIZE = 25 * 1024 * 1024; // 25MB cap for offline buffering
const genUuid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

const TicketCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isComplaint = searchParams.get('isComplaint') === 'true';

  // Form state
  const [formData, setFormData] = useState({
    // Issue fields
    type_id: '',
    description: '',
    internal_note: '',
    urgency_id: '',
    cost_of_works: '',
    assigned_member_id: '',

    // Property/Room fields
    property_id: '',
    room_id: '',
    issue_with: 'room', // For guardians: room or property
    guardian_id: '', // Guardian ID for guardian-created tickets

    // Contractor fields
    contractor_id: '',
    contractor_contact_id: '',
    equipment_purchased: '',
    is_complaint: isComplaint ? 1 : 0,
    status_id: '',
    complaint_id: '',
    date_completed: '',
    book_in_date: '',
    book_in_time_from: '',
    book_in_time_to: '',
    client_view: 0,
    guardian_view: 1, // Default to 1 (visible to guardians)
    is_certificate_renewal: 0, // Staff-only: enables the certificate upload/review flow

    // Files
    attachments: []
  });

  // Form data from API
  const [dropdowns, setDropdowns] = useState({
    ticketTypes: [],
    urgencies: [],
    statuses: [],
    complaintStatuses: [],
    users: [],
    contractors: [],
    contractorContacts: [],
    properties: [],
    rooms: []
  });

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [propertySearchText, setPropertySearchText] = useState('');
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  const [searchingProperties, setSearchingProperties] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchRooms, setSearchRooms] = useState([]);
  const propertyDropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Get rooms for selected property (from search results)
  const propertyRooms = searchRooms.filter(
    (r) => String(r.property_id) === String(formData.property_id)
  );

  // Load form data
  useEffect(() => {
    loadFormData();
  }, [isComplaint]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (propertyDropdownRef.current && !propertyDropdownRef.current.contains(event.target)) {
        setShowPropertyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debounced property search via API
  const searchProperties = (query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setSearchRooms([]);
      setSearchingProperties(false);
      return;
    }

    setSearchingProperties(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/tickets/search-properties?q=${encodeURIComponent(query.trim())}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.properties || []);
          setSearchRooms(prev => {
            // Merge new rooms with existing ones (keep rooms for already-selected property)
            const existingIds = new Set((data.rooms || []).map(r => r.id));
            const kept = prev.filter(r => !existingIds.has(r.id) && String(r.property_id) === String(formData.property_id));
            return [...kept, ...(data.rooms || [])];
          });
        }
      } catch (err) {
        console.error("Property search error:", err);
      } finally {
        setSearchingProperties(false);
      }
    }, 300);
  };

  const loadFormData = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    
    try {
      setLoading(true);
      setError(null);

      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error("Request timeout after 60 seconds")),
          60000
        );
      });

      console.log(
        `🔍 [TICKET CREATE] Fetching form data${retryCount > 0 ? ` (retry ${retryCount})` : ''}`
      );

      const fetchPromise = fetch(
        `${process.env.REACT_APP_API_URL}/api/tickets/create?isComplaint=${isComplaint}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const response = await Promise.race([fetchPromise, timeoutPromise]);

      console.log("📊 [TICKET CREATE] Response status:", response.status);
      console.log("📊 [TICKET CREATE] Response headers:", response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ [TICKET CREATE] Response not OK:", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });
        throw new Error(
          `Failed to load form data: ${response.status} ${response.statusText}`
        );
      }

      // Safely parse JSON response
      let data;
      let responseText = null;
      try {
        responseText = await response.text();
        console.log(
          "📊 [TICKET CREATE] Response text length:",
          responseText.length
        );

        // Check if response is too large
        if (responseText.length > 10 * 1024 * 1024) {
          // 10MB limit
          console.warn(
            "⚠️ [TICKET CREATE] Response is very large:",
            responseText.length,
            "bytes"
          );
        }

        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("❌ [TICKET CREATE] JSON parse error:", parseError);
        console.error("❌ [TICKET CREATE] Response status:", response.status);
        console.error("❌ [TICKET CREATE] Response headers:", Object.fromEntries(response.headers.entries()));
        console.error("❌ [TICKET CREATE] Response text length:", responseText?.length || 0);
        
        // Retry on Content-Length mismatch or parse errors
        if (retryCount < MAX_RETRIES && (
          parseError.message.includes('JSON') || 
          parseError.message.includes('parse') ||
          response.status === 200
        )) {
          console.log(`🔄 [TICKET CREATE] Retrying request (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
          return loadFormData(retryCount + 1);
        }
        
        throw new Error(
          `Failed to parse response: ${parseError.message}`
        );
      }

      console.log("=== FORM DATA LOAD DEBUG ===");
      console.log("API response data:", data);
      console.log("complaintStatuses:", data.complaintStatuses);
      console.log("statuses:", data.statuses);
      console.log("📋 Properties loaded:", data.properties?.length || 0);
      console.log("📋 Rooms loaded:", data.rooms?.length || 0);

      // Validate response structure
      if (!data || typeof data !== "object") {
        throw new Error("Invalid response structure received from server");
      }

      setDropdowns({
        ticketTypes: data.ticketTypes || [],
        urgencies: data.urgencies || [],
        statuses: data.statuses || [],
        complaintStatuses: data.complaintStatuses || [],
        users: data.users || [],
        contractors: data.contractors || [],
        contractorContacts: data.contractorContacts || [],
        properties: data.properties || [],
        rooms: data.rooms || [],
      });

      setCurrentUser(data.currentUser);
      

      
      // Set default values based on user role
      if (data.currentUser?.isGuardian) {
        // For guardians, auto-assign their property, room, and guardian_id
        const guardianProperty = data.currentUser.property;
        const guardianRoom = data.currentUser.room;
        const guardianId = data.currentUser.guardianId;

        console.log("🏠 Guardian data from API:", {
          property: guardianProperty,
          room: guardianRoom,
          guardianId: guardianId
        });

        setFormData(prev => ({
          ...prev,
          assigned_member_id: data.currentUser.id,
          property_id: guardianProperty?.id || '',
          room_id: guardianRoom?.id || '',
          guardian_id: guardianId || '',
          issue_with: 'room' // Default to room-level issues for guardians
        }));

        // Set the selected property for display
        if (guardianProperty) {
          setSelectedProperty(guardianProperty);
        }
      }
    } catch (err) {
      console.error("❌ [TICKET CREATE] Error loading form data:", err);
      console.error("❌ [TICKET CREATE] Error details:", {
        message: err.message,
        name: err.name,
        stack: err.stack,
      });
      
      // Retry on network errors (Failed to fetch, Content-Length mismatch, etc.)
      if (retryCount < MAX_RETRIES && (
        err.message.includes('Failed to fetch') ||
        err.message.includes('ERR_CONTENT_LENGTH_MISMATCH') ||
        err.message.includes('network') ||
        err.name === 'TypeError'
      )) {
        console.log(`🔄 [TICKET CREATE] Retrying request due to network error (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
        return loadFormData(retryCount + 1);
      }

      // More specific error messages
      let errorMessage = "Failed to load form data";
      if (err.message?.includes("timeout")) {
        errorMessage =
          "Request timed out. The server may be processing a large amount of data. Please try again.";
      } else if (err.message?.includes("Failed to parse")) {
        errorMessage =
          "Server returned invalid data. Please contact support if this persists.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handlePropertyChange = (propertyId) => {
    console.log('🏢 Property selected:', propertyId, 'Type:', typeof propertyId);

    setFormData(prev => ({
      ...prev,
      property_id: propertyId,
      room_id: '' // Reset room when property changes
    }));

    // Handle both string and number comparisons for property ID
    // Search in both searchResults and dropdowns.properties (for guardian auto-assigned)
    const property = searchResults.find(p =>
      String(p.id) === String(propertyId)
    ) || dropdowns.properties.find(p =>
      String(p.id) === String(propertyId)
    );

    console.log('🏢 Property data found:', property);

    setSelectedProperty(property);

    // Update search text with selected property
    if (property) {
      setPropertySearchText(`${property.property_id} - ${property.name}`);
    }
    setShowPropertyDropdown(false);

    // Check if we need to make any additional API calls for property selection
    if (propertyId && property) {
      console.log('🏢 Property selected successfully:', property.name);
    } else if (propertyId && !property) {
      console.error('🏢 Property not found for ID:', propertyId);
      console.error('🏢 Available property IDs:', dropdowns.properties.map(p => p.id));
    }
  };

  // Use API search results for property dropdown
  const filteredProperties = searchResults;

  const handleRoomChange = (roomId) => {
    setFormData(prev => ({
      ...prev,
      room_id: roomId
    }));
    
    // Room selection handled by form data
  };

  const handleContractorChange = (contractorId) => {
    setFormData(prev => ({
      ...prev,
      contractor_id: contractorId,
      contractor_contact_id: '' // Reset contact when contractor changes
    }));
  };

  const handleMultipleFilesUpload = (filesData) => {
    console.log('Multiple files uploaded:', filesData);
    setUploadedDocuments(filesData);
    setError(null); // Clear any previous errors
  };

  const validate = () => {
    const errors = [];

    // Required fields for all users
    if (!formData.type_id) errors.push('Ticket type is required');
    if (!formData.description) errors.push('Description is required');
    if (!formData.urgency_id) errors.push('Urgency is required');

    // Property/Room validation - Guardians get their property auto-assigned
    if (!currentUser?.isGuardian && !formData.property_id) {
      errors.push('Property is required');
    }

    // Guardian-specific validation - issue_with is required
    if (currentUser?.isGuardian && !formData.issue_with) {
      errors.push('Please select what the issue is with (room or communal area)');
    }

    // Contractor contact validation (staff only)
    if (!currentUser?.isGuardian && formData.contractor_id && !formData.contractor_contact_id) {
      errors.push('Contractor contact is required when a contractor is selected');
    }

    // Time validation (staff only)
    if (!currentUser?.isGuardian && formData.book_in_time_from && formData.book_in_time_to && formData.book_in_time_to <= formData.book_in_time_from) {
      errors.push('Book in Time To must be after Book in Time From');
    }

    // Staff-only fields - Status validation (either status_id OR complaint_id based on is_complaint)
    // Exclude: Guardians and Sales with Guardians role
    if (currentUser?.isStaff && !currentUser?.isGuardian && !currentUser?.isSalesWithGuardians) {
      if (formData.is_complaint === 1) {
        // For complaints, require complaint_id
        if (!formData.complaint_id) {
          errors.push('Complaint status is required');
        }
      } else {
        // For regular tickets, require status_id
        if (!formData.status_id) {
          errors.push('Status is required');
        }
      }

      // Assigned Member is required for staff (excluding Guardians and Sales with Guardians)
      if (!formData.assigned_member_id) {
        errors.push('Assigned Member is required');
      }
    }

    return errors;
  };

  const submittingRef = useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validate();
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    // Hard guard: never let two submits run at once (double-click / Enter+click),
    // which would create multiple ticket drafts.
    if (submittingRef.current) return;
    submittingRef.current = true;

    setSaving(true);
    setError(null);

    const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    const hasOfflineAttachments = uploadedDocuments.some((d) => d.offline);

    // Offline, or attachments still buffered → queue the ticket for background sync.
    if (!isOnline || hasOfflineAttachments) {
      const queued = await queueTicketOffline();
      if (!queued) {
        setSaving(false);
        submittingRef.current = false;
      }
      return;
    }

    try {
      // Prepare data as JSON (not FormData since we're using S3 for files)
      const requestData = {
        ...formData,
        // Add uploaded documents (S3 keys)
        attachments: uploadedDocuments.length > 0 ? uploadedDocuments.map(doc => ({
          name: doc.name,
          s3Key: doc.s3Key,
          size: doc.size,
          type: doc.type
        })) : undefined
      };

      console.log('📎 Submitting ticket with attachments:', {
        totalFields: Object.keys(requestData).length,
        attachments: requestData.attachments?.length || 0,
        attachmentDetails: requestData.attachments
      });

      console.log("Request data being sent:", requestData);
      console.log("API URL:", `${process.env.REACT_APP_API_URL}/api/tickets`);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.log("Error response:", errorData);
        console.log("Response status:", response.status);
        throw new Error(errorData.message || 'Failed to create ticket');
      }

      const data = await response.json();
      console.log('Ticket creation response:', data);
      setSuccess(true);

      // Guardians always redirect to tickets list (matches Laravel behavior)
      if (currentUser?.isGuardian) {
        setTimeout(() => {
          navigate('/tickets');
        }, 1000);
      } else {
        // Staff: Redirect to ticket detail page if we have a valid ID
        if (data.ticket && data.ticket.id) {
          setTimeout(() => {
            navigate(`/tickets/${data.ticket.id}`);
          }, 1000);
        } else if (data.id) {
          // Fallback: check if ID is directly in response
          setTimeout(() => {
            navigate(`/tickets/${data.id}`);
          }, 1000);
        } else {
          // If no valid ID, redirect to tickets list
          setTimeout(() => {
            navigate('/tickets');
          }, 1000);
        }
      }
    } catch (err) {
      // Network failure (fetch reject / offline) → queue instead of losing it.
      if (err?.name === 'TypeError' || (typeof navigator !== 'undefined' && !navigator.onLine)) {
        const queued = await queueTicketOffline();
        if (queued) return;
      }
      setError(err.message);
      setSaving(false);
      submittingRef.current = false;
    }
  };

  // Queue the ticket (form + any attachments) for background sync. Returns true
  // if queued. Attachments already on S3 keep their key; offline ones buffer.
  const queueTicketOffline = async () => {
    const tooBig = uploadedDocuments.find(
      (d) => d.blob && !d.s3Key && d.size > OFFLINE_MAX_SIZE
    );
    if (tooBig) {
      setError(
        `While offline, attachments must be under ${OFFLINE_MAX_SIZE / (1024 * 1024)}MB. Add larger files when you’re back online.`
      );
      return false;
    }

    try {
      const photos = {};
      uploadedDocuments.forEach((doc) => {
        const id = genUuid();
        photos[id] = doc.s3Key
          ? {
              s3Key: doc.s3Key,
              fileName: doc.name,
              mimeType: doc.type,
              size: doc.size,
              folder: 'documents',
            }
          : {
              blob: doc.blob,
              fileName: doc.name,
              mimeType: doc.type,
              size: doc.size,
              folder: 'documents',
            };
      });

      const { attachments, ...formFields } = formData; // attachments rebuilt from photos on sync
      await saveDraft({
        id: genUuid(),
        ticketId: null,
        reportType: 'ticketForm',
        mode: 'create',
        formData: formFields,
        photos,
        clientUuid: genUuid(),
        status: 'queued',
        attempts: 0,
        permanent: false,
        lastError: null,
      });
    } catch (err) {
      setError(
        isQuotaError(err)
          ? 'This device is out of storage, so the ticket could not be saved for offline upload.'
          : 'Offline storage is unavailable, so the ticket cannot be saved while offline.'
      );
      return false;
    }

    toast.info('Saved. This ticket will be created automatically when you’re back online.');
    triggerSync();
    navigate('/tickets');
    return true;
  };

  if (loading) return <Loader />;
  // if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Breadcrumbs */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 0 0 0' }}>
        <div style={{ fontSize: 15, marginBottom: 16 }}>
          <span style={{ color: '#007bff', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>Dashboard</span>
          <span style={{ color: '#bbb' }}> / </span>
          <span style={{ color: '#007bff', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/tickets')}>Tickets</span>
          <span style={{ color: '#bbb' }}> / </span>
          <span style={{ background: '#36bba5', color: '#fff', padding: '2px 8px', borderRadius: 3, fontWeight: 500 }}>{isComplaint ? 'Create Complaint' : 'Create Ticket'}</span>
        </div>
      </div>

      {/* Main Card */}
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ background: '#fafbfc', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: 40, margin: '0 auto', marginTop: 0, marginBottom: 32, width: '100%' }}>
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            {/* Error Message Display */}
            {error && (
              <div style={{ color: '#721c24', background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 4, padding: 16, marginBottom: 24, fontWeight: 500 }}>
                {error}
              </div>
            )}

            {/* Section: Submit new ticket - Only show for staff */}
            {!currentUser?.isGuardian && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 24, marginBottom: 12, textAlign: 'left' }}>Submit new {isComplaint ? 'complaint' : 'ticket'}</div>
                <div style={{ color: '#444', fontSize: 15, marginBottom: 8 }}>
                  <strong>Step 1</strong> Select property at which you want to create {isComplaint ? 'complaint' : 'ticket'}.<br />
                  <strong>Step 2</strong> Fill {isComplaint ? 'complaint' : 'ticket'} details.<br />
                  <strong>Step 3</strong> Assign Contractor to {isComplaint ? 'complaint' : 'ticket'}.<br />
                </div>
              </div>
            )}

            {/* Guardian Layout - Two columns side by side like Laravel */}
            {currentUser?.isGuardian && selectedProperty ? (
              <div style={{ display: 'flex', gap: 32, marginBottom: 24, flexWrap: 'wrap' }}>
                {/* Left Column: Your property overview (5/12 width like Laravel col-md-5) */}
                <div style={{ flex: '0 0 41.66%', minWidth: 300 }}>
                  <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8, borderBottom: '2px solid #36bba5', paddingBottom: 8 }}>Your property overview</div>
                  <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 4, padding: 16, textAlign: 'center' }}>
                    <dl style={{ margin: 0, textAlign: 'center' }}>
                      <div style={{ marginBottom: 8 }}>
                        <dt style={{ display: 'inline', fontWeight: 600 }}>Property ID:</dt>{' '}
                        <dd style={{ display: 'inline', margin: 0 }}>{selectedProperty.property_id}</dd>
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <dt style={{ display: 'inline', fontWeight: 600 }}>Property name:</dt>{' '}
                        <dd style={{ display: 'inline', margin: 0 }}>{selectedProperty.name}</dd>
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <dt style={{ display: 'inline', fontWeight: 600 }}>Property address:</dt>{' '}
                        <dd style={{ display: 'inline', margin: 0 }}>{selectedProperty.name}</dd>
                      </div>
                      <div style={{ marginBottom: 8 }}>&nbsp;</div>
                      <div>
                        <dt style={{ display: 'inline', fontWeight: 600 }}>Postcode:</dt>{' '}
                        <dd style={{ display: 'inline', margin: 0 }}>{selectedProperty.postcode || "-"}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {/* Right Column: Issue section (7/12 width like Laravel col-md-7) */}
                <div style={{ flex: '0 0 55%', minWidth: 400 }}>
                  <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8, borderBottom: '2px solid #36bba5', paddingBottom: 8 }}>Issue</div>
                  <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 4, padding: 24 }}>
                    {/* Issue with */}
                    <div style={{ marginBottom: 16 }}>
                      <label className="control-label label-required" style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Issue With <span style={{ color: '#d9534f' }}>*</span></label>
                      <select
                        name="issue_with"
                        style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                        value={formData.issue_with}
                        onChange={handleChange}
                        required
                      >
                        <option value="room">My room</option>
                        <option value="property">Communal area</option>
                      </select>
                    </div>

                    {/* Ticket Type */}
                    <div style={{ marginBottom: 16 }}>
                      <label className="control-label label-required" style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Type <span style={{ color: '#d9534f' }}>*</span></label>
                      <select
                        name="type_id"
                        style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                        value={formData.type_id}
                        onChange={handleChange}
                        required
                      >
                        <option value="">--- Select ticket type ---</option>
                        {dropdowns.ticketTypes.map(type => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Description */}
                    <div style={{ marginBottom: 16 }}>
                      <label className="control-label label-required" style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Description <span style={{ color: '#d9534f' }}>*</span></label>
                      <textarea
                        name="description"
                        style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                        rows="5"
                        value={formData.description}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Urgency */}
                    <div style={{ marginBottom: 16 }}>
                      <label className="control-label label-required" style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Urgency <span style={{ color: '#d9534f' }}>*</span></label>
                      <select
                        name="urgency_id"
                        style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                        value={formData.urgency_id}
                        onChange={handleChange}
                        required
                      >
                        <option value="">--- Select ticket urgency ---</option>
                        {dropdowns.urgencies.map(urgency => (
                          <option key={urgency.id} value={urgency.id}>
                            {urgency.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Attachments */}
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 8, display: 'block' }}>Attachments</label>
                      <S3MultipleFileUpload
                        onFilesUpload={handleMultipleFilesUpload}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.bmp,.zip,.rar,.7z"
                        maxSize={100 * 1024 * 1024}
                        helpText="Upload documents related to this ticket. Allowed formats: PDF, DOC, DOCX, XLS, XLSX, TXT, CSV, images, Archives. Maximum file size: 100MB"
                        folder="documents"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Staff Layout - Original layout */
              <>
                {/* Section: Choose property & Property overview */}
                <div style={{ display: 'flex', gap: 32, marginBottom: 24, flexWrap: 'wrap' }}>
                  {/* Choose property */}
                  {!currentUser?.isGuardian && (
                    <div style={{ flex: 1, minWidth: 260 }}>
                      <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Choose property</div>
                      <div style={{ marginBottom: 8, position: 'relative' }} ref={propertyDropdownRef}>
                        <label htmlFor="property_search" style={{ fontWeight: 500, fontSize: 15, marginBottom: 4, display: 'block' }}>Search for property</label>
                        <div style={{ position: 'relative' }}>
                          <input
                            id="property_search"
                            type="text"
                            placeholder="Type to search properties..."
                            style={{
                              width: '100%',
                              padding: '8px 32px 8px 12px',
                              fontSize: 15,
                              border: '1px solid #ccc',
                              borderRadius: 4,
                              background: '#fff',
                              fontWeight: 400
                            }}
                            value={propertySearchText}
                            onChange={(e) => {
                              setPropertySearchText(e.target.value);
                              setShowPropertyDropdown(true);
                              searchProperties(e.target.value);
                            }}
                            onFocus={() => setShowPropertyDropdown(true)}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPropertyDropdown(!showPropertyDropdown)}
                            style={{
                              position: 'absolute',
                              right: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 4,
                              color: '#666'
                            }}
                          >
                            ▼
                          </button>
                        </div>
                        {showPropertyDropdown && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: 300,
                            overflowY: 'auto',
                            background: '#fff',
                            border: '1px solid #ccc',
                            borderTop: 'none',
                            borderRadius: '0 0 4px 4px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            zIndex: 1000
                          }}>
                            {searchingProperties ? (
                              <div style={{ padding: '8px 12px', color: '#999' }}>
                                Searching...
                              </div>
                            ) : propertySearchText.trim().length < 2 ? (
                              <div style={{ padding: '8px 12px', color: '#999' }}>
                                Type at least 2 characters to search
                              </div>
                            ) : filteredProperties.length > 0 ? (
                              filteredProperties.map(property => (
                                <div
                                  key={property.id}
                                  onClick={() => handlePropertyChange(property.id)}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #eee',
                                    background: formData.property_id === property.id ? '#f0f0f0' : '#fff'
                                  }}
                                  onMouseEnter={(e) => e.target.style.background = '#f8f8f8'}
                                  onMouseLeave={(e) => e.target.style.background = formData.property_id === property.id ? '#f0f0f0' : '#fff'}
                                >
                                  {property.property_id} - {property.name}
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '8px 12px', color: '#999' }}>
                                No properties found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Property overview */}
                  <div style={{ flex: 2, minWidth: 320 }}>
                    <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Property overview</div>
                    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 4, padding: 16, minHeight: 120 }}>
                      {console.log('🏢 Rendering overview - selectedProperty:', selectedProperty, 'formData.property_id:', formData.property_id)}
                      {selectedProperty ? (
                        <>
                          <div className="property-overview">
                            <div><dt>Property ID:</dt> <dd>{selectedProperty.property_id}</dd></div>
                            <div><dt>Property name:</dt> <dd>{selectedProperty.name}</dd></div>
                            <div><dt>Property address:</dt> <dd>
                              {selectedProperty.address_1 && <div>{selectedProperty.address_1}</div>}
                              {selectedProperty.address_2 && <div>{selectedProperty.address_2}</div>}
                              {selectedProperty.address_3 && <div>{selectedProperty.address_3}</div>}
                              {!selectedProperty.address_1 && !selectedProperty.address_2 && !selectedProperty.address_3 && (
                                <div>{selectedProperty.name}</div>
                              )}
                            </dd></div>
                            <div className="postcode-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <dt>Postcode:</dt>
                              <dd style={{ margin: 0 }}>{selectedProperty.postcode || "-"}</dd>
                            </div>
                          </div>

                          {!currentUser?.isGuardian && (
                            <>
                              <div style={{ fontWeight: 600, fontSize: 15, marginTop: 16, marginBottom: 12 }}>Select rooms</div>
                              {propertyRooms.length === 0 ? (
                                <div style={{ color: "#888", fontSize: 14 }}>
                                  No rooms found for this property.
                                </div>
                              ) : (
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "12px" }}>
                                  {propertyRooms.map((room) => (
                                    <div key={room.id} style={{ background: '#e0f2f1', borderRadius: 4, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                                      <div style={{ fontWeight: 700, color: '#009688', marginBottom: 4 }}>Room {room.name}</div>
                                      <div style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>Number of keys: {room.key_number || 0}</div>
                                      <button
                                        type="button"
                                        className="btn-action"
                                        style={{
                                          background: String(formData.room_id) === String(room.id) ? '#4caf50' : '#009688',
                                          color: '#fff',
                                          fontWeight: 600,
                                          padding: '6px 18px',
                                          border: 'none',
                                          borderRadius: 4,
                                          cursor: 'pointer',
                                          width: '100%',
                                          fontSize: 14
                                        }}
                                        onClick={() => handleRoomChange(room.id)}
                                      >
                                        {String(formData.room_id) === String(room.id) ? '✓ Selected room' : '› Select this room'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <div style={{ color: '#d9534f', fontSize: 14, padding: 8, background: '#fff3cd', border: '1px solid #ffeeba', borderRadius: 4 }}>
                          Property is required to create a {isComplaint ? 'complaint' : 'ticket'}. Please choose one to continue...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Section: Issue - Only show for staff (guardians have it in the two-column layout above) */}
            {!currentUser?.isGuardian && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Issue</div>
                <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 4, padding: 24 }}>
                  {/* Ticket Type */}
                  <div style={{ marginBottom: 16 }}>
                    <label className="control-label label-required" style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Type</label>
                    <select
                      name="type_id"
                      style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                      value={formData.type_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">--- Select ticket type ---</option>
                      {dropdowns.ticketTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: 16 }}>
                    <label className="control-label label-required" style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Description</label>
                    <textarea
                      name="description"
                      style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                      rows="5"
                      value={formData.description}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Internal Note (staff only) */}
                  {currentUser?.isStaff && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Internal Note</label>
                      <textarea
                        name="internal_note"
                        style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                        rows="5"
                        value={formData.internal_note}
                        onChange={handleChange}
                      />
                    </div>
                  )}

                  {/* Proposed Deadline (staff only) */}
                  {currentUser?.isStaff && (
                    <div style={{ marginBottom: 16 }}>
                      <label className="control-label label-required" style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Proposed deadline</label>
                      <input
                        type="date"
                        name="date_estimated_completion"
                        min={new Date().toISOString().split('T')[0]}
                        style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                        value={formData.date_estimated_completion}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  )}

                  {/* Certificate renewal (staff only, certification ticket types only) */}
                  {currentUser?.isStaff &&
                    !!certificateTypeFromServiceName(
                      dropdowns.ticketTypes?.find(
                        (t) => String(t.id) === String(formData.type_id)
                      )?.name
                    ) && (
                    <div
                      style={{ marginBottom: 16 }}
                      title="Only renewal tickets show the certificate upload panel and go through the review queue."
                    >
                      <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Certificate Renewal</label>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          name="is_certificate_renewal"
                          checked={formData.is_certificate_renewal === 1}
                          onChange={(e) =>
                            setFormData(prev => ({ ...prev, is_certificate_renewal: e.target.checked ? 1 : 0 }))
                          }
                          style={{ transform: 'scale(1.1)' }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Client View (staff only) */}
                  {currentUser?.isStaff && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Client View</label>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label>
                          <input
                            type="radio"
                            name="client_view"
                            value="0"
                            checked={formData.client_view === 0}
                            onChange={(e) => setFormData(prev => ({ ...prev, client_view: Number(e.target.value) }))}
                            style={{ marginRight: 10, transform: 'scale(1.1)' }}
                          /> No
                        </label>
                        <label style={{ marginLeft: 20 }}>
                          <input
                            type="radio"
                            name="client_view"
                            value="1"
                            checked={formData.client_view === 1}
                            onChange={(e) => setFormData(prev => ({ ...prev, client_view: Number(e.target.value) }))}
                            style={{ marginRight: 10, transform: 'scale(1.1)' }}
                          /> Yes
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Guardian View (staff only) */}
                  {currentUser?.isStaff && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Guardian View</label>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label>
                          <input
                            type="radio"
                            name="guardian_view"
                            value="0"
                            checked={formData.guardian_view === 0}
                            onChange={(e) => setFormData(prev => ({ ...prev, guardian_view: Number(e.target.value) }))}
                            style={{ marginRight: 10, transform: 'scale(1.1)' }}
                          /> No
                        </label>
                        <label style={{ marginLeft: 20 }}>
                          <input
                            type="radio"
                            name="guardian_view"
                            value="1"
                            checked={formData.guardian_view === 1}
                            onChange={(e) => setFormData(prev => ({ ...prev, guardian_view: Number(e.target.value) }))}
                            style={{ marginRight: 10, transform: 'scale(1.1)' }}
                          /> Yes
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Urgency */}
                  <div style={{ marginBottom: 16 }}>
                    <label className="control-label label-required" style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Urgency</label>
                    <select
                      name="urgency_id"
                      style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                      value={formData.urgency_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">--- Select ticket urgency ---</option>
                      {dropdowns.urgencies.map(urgency => (
                        <option key={urgency.id} value={urgency.id}>
                          {urgency.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Cost of Works (staff only) */}
                  {(currentUser?.isStaff || currentUser?.staff === 1) && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Cost of works</label>
                      <input
                        type="number"
                        name="cost_of_works"
                        style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                        value={formData.cost_of_works}
                        onChange={handleChange}
                        step="0.01"
                      />
                    </div>
                  )}

                  {/* Assigned Member (staff only) */}
                  {(currentUser?.isStaff || currentUser?.staff === 1) && (
                    <div style={{ marginBottom: 16 }}>
                      <label className={!currentUser?.isGuardian && !currentUser?.isSalesWithGuardians ? "control-label label-required" : "control-label"} style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>
                        Assigned Member
                        {!currentUser?.isGuardian && !currentUser?.isSalesWithGuardians && <span style={{ color: '#d9534f' }}> *</span>}
                      </label>
                      <select
                        name="assigned_member_id"
                        style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                        value={formData.assigned_member_id}
                        onChange={handleChange}
                        required={!currentUser?.isGuardian && !currentUser?.isSalesWithGuardians}
                      >
                        <option value="">--- Select user ---</option>
                        {Array.isArray(dropdowns.users) ? dropdowns.users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        )) : (
                          <option value="">No users available</option>
                        )}
                      </select>
                    </div>
                  )}

                  {/* Attachments */}
                  <div style={{ marginBottom: 24 }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 16
                    }}>
                      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#333' }}>
                        Attachments
                      </h3>
                    </div>

                    <S3MultipleFileUpload
                      onFilesUpload={handleMultipleFilesUpload}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.bmp,.zip,.rar,.7z"
                      maxSize={100 * 1024 * 1024} // 100MB
                      helpText="Upload documents related to this ticket. Allowed formats: PDF, DOC, DOCX, XLS, XLSX, TXT, CSV, images, Archives. Maximum file size: 100MB"
                      folder="documents"
                    />
                  </div>

                </div>
              </div>
            )}

            {/* Section: Contractor - Only show for staff, hide for guardians */}
            {!currentUser?.isGuardian && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Contractor</div>
              <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 4, padding: 24 }}>
                {/* Contractor */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Contractor</label>
                  <select
                    name="contractor_id"
                    style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                    value={formData.contractor_id}
                    onChange={(e) => handleContractorChange(e.target.value)}
                  >
                    <option value="">--- Select contractor ---</option>
                    {dropdowns.contractors.map(contractor => (
                      <option key={contractor.id} value={contractor.id}>
                        {contractor.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Contractor Contact */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>
                    Contractor Contact
                    {formData.contractor_id && <span style={{ color: '#d9534f', marginLeft: 4 }}>*</span>}
                  </label>
                  <select
                    name="contractor_contact_id"
                    style={{ 
                      width: '100%', 
                      padding: '8px 12px', 
                      fontSize: 15, 
                      border: formData.contractor_id && !formData.contractor_contact_id ? '1px solid #d9534f' : '1px solid #ccc', 
                      borderRadius: 4, 
                      background: '#fff', 
                      fontWeight: 400 
                    }}
                    value={formData.contractor_contact_id}
                    onChange={handleChange}
                    required={formData.contractor_id}
                  >
                    <option value="">--- Select contact ---</option>
                    {formData.contractor_id && dropdowns.contractorContacts
                      .filter(contact => String(contact.contractor_id) === String(formData.contractor_id))
                      .map(contact => (
                        <option key={contact.id} value={contact.id}>
                          {contact.name}
                        </option>
                      ))}
                  </select>
                  {formData.contractor_id && !formData.contractor_contact_id && (
                    <div style={{ color: '#d9534f', fontSize: 12, marginTop: 4 }}>
                      Contractor contact is required when a contractor is selected
                    </div>
                  )}
                </div>

                {/* Equipment Purchased */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Equipment Purchased</label>
                  <textarea
                    name="equipment_purchased"
                    style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                    rows="3"
                    value={formData.equipment_purchased}
                    onChange={handleChange}
                  />
                </div>

                {/* Is Complaint */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Is Complaint?</label>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label>
                      <input
                        type="radio"
                        name="is_complaint"
                        value={0}
                        checked={formData.is_complaint === 0}
                        onChange={e => setFormData(prev => ({ ...prev, is_complaint: Number(e.target.value) }))}
                        style={{ marginRight: 10, transform: 'scale(1.1)' }}
                      /> No
                    </label>
                    <label style={{ marginLeft: 20 }}>
                      <input
                        type="radio"
                        name="is_complaint"
                        value={1}
                        checked={formData.is_complaint === 1}
                        onChange={e => setFormData(prev => ({ ...prev, is_complaint: Number(e.target.value) }))}
                        style={{ marginRight: 10, transform: 'scale(1.1)' }}
                      /> Yes
                    </label>
                  </div>
                </div>

                {/* Ticket Status */}
                {formData.is_complaint === 1 ? (
                  <div style={{ marginBottom: 16 }}>
                    <label className="control-label label-required" style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Complaint Status</label>
                    <select
                      name="complaint_id"
                      style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                      value={formData.complaint_id}
                      onChange={(e) => {
                        console.log("Complaint status changed:", e.target.value);
                        handleChange(e);
                      }}
                      required
                    >
                      <option value="">Please select</option>
                      {dropdowns.complaintStatuses.map(status => (
                        <option key={status.id} value={status.id}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      Available options: {dropdowns.complaintStatuses.length} statuses
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom: 16 }}>
                    <label className="control-label label-required" style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Ticket Status</label>
                    <select
                      name="status_id"
                      style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                      value={formData.status_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Please select</option>
                      {dropdowns.statuses.map(status => (
                        <option key={status.id} value={status.id}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Ticket Complete Date */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Ticket Complete Date</label>
                  <input
                    type="date"
                    name="date_completed"
                    style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                    value={formData.date_completed}
                    onChange={handleChange}
                  />
                </div>

                {/* Book In Date */}
                <div style={{ marginBottom: 16 }}>
                  <label className="control-label label-required" style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>Book In Day</label>
                  <input
                    type="date"
                    name="book_in_date"
                    min={new Date().toISOString().split('T')[0]}
                    style={{ width: '100%', padding: '8px 12px', fontSize: 15, border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontWeight: 400 }}
                    value={formData.book_in_date}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Book In Time From */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>
                    Book in Time From
                    {(formData.book_in_time_from || formData.book_in_time_to) && <span style={{ color: '#d9534f', marginLeft: 4 }}>*</span>}
                  </label>
                  <input
                    type="time"
                    name="book_in_time_from"
                    style={{ 
                      width: '100%', 
                      padding: '8px 12px', 
                      fontSize: 15, 
                      border: (formData.book_in_time_to && !formData.book_in_time_from) ? '1px solid #d9534f' : '1px solid #ccc', 
                      borderRadius: 4, 
                      background: '#fff', 
                      fontWeight: 400 
                    }}
                    value={formData.book_in_time_from}
                    onChange={handleChange}
                    required={!!formData.book_in_time_to}
                  />
                  {formData.book_in_time_to && !formData.book_in_time_from && (
                    <div style={{ color: '#d9534f', fontSize: 12, marginTop: 4 }}>
                      Start time is required when end time is selected
                    </div>
                  )}
                </div>

                {/* Book In Time To */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, fontSize: 15, marginBottom: 4 }}>
                    Book in Time To
                    {(formData.book_in_time_from || formData.book_in_time_to) && <span style={{ color: '#d9534f', marginLeft: 4 }}>*</span>}
                  </label>
                  <input
                    type="time"
                    name="book_in_time_to"
                    min={formData.book_in_time_from || undefined}
                    style={{ 
                      width: '100%', 
                      padding: '8px 12px', 
                      fontSize: 15, 
                      border: (formData.book_in_time_from && !formData.book_in_time_to) ? '1px solid #d9534f' : '1px solid #ccc', 
                      borderRadius: 4, 
                      background: '#fff', 
                      fontWeight: 400 
                    }}
                    value={formData.book_in_time_to}
                    onChange={handleChange}
                    required={!!formData.book_in_time_from}
                  />
                  {formData.book_in_time_from && !formData.book_in_time_to && (
                    <div style={{ color: '#d9534f', fontSize: 12, marginTop: 4 }}>
                      End time is required when start time is selected
                    </div>
                  )}
                  {formData.book_in_time_from && formData.book_in_time_to && formData.book_in_time_to <= formData.book_in_time_from && (
                    <div style={{ color: '#d9534f', fontSize: 12, marginTop: 4 }}>
                      End time must be after start time
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}

            {/* Section: Confirmation */}
            <div style={{ marginBottom: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Confirmation</div>
              <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 4, padding: 24, textAlign: 'center' }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ background: '#223a5e', color: '#fff', border: 'none', borderRadius: 6, fontSize: 18, fontWeight: 700, padding: '12px 48px', minWidth: 180, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
                >
                  {saving ? (
                    <span>
                      <svg style={{ marginRight: 8 }} width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" stroke="#fff" strokeOpacity=".2" /><path d="M12 6v6l4 2" stroke="#fff" /></svg>
                      Creating...
                    </span>
                  ) : (
                    <span>
                      <svg style={{ marginRight: 8 }} width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                      {isComplaint ? 'Submit complaint' : 'Submit new ticket'}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Success Message */}
            {success && (
              <div style={{ color: '#155724', background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 4, padding: 16, marginTop: 24, fontWeight: 600, textAlign: 'center' }}>
                <svg style={{ marginRight: 8, verticalAlign: 'middle' }} width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#155724" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                {isComplaint ? 'Complaint' : 'Ticket'} created successfully! Redirecting...
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default TicketCreate; 